// CopilotPanel.jsx — Moonshot #2b: whisper-mode co-pilot cards.
//
// Sits next to the live-transcript pane in PhonePanel. Subscribes to the
// server-side `copilot:suggestion` socket.io event (tenant-scoped) and
// renders a stack of compact cards. Newest on top, fade out at 30s if not
// used. "Used it" POSTs to /api/copilot/suggestions/:id/used.
//
// Tenant-scoping is enforced server-side; we just filter by current callId
// in case multiple calls land on the same socket.

import { useEffect, useRef, useState } from 'react';
import { getSocket } from '../lib/socket.js';
import { apiPost } from '../lib/api.js';

const FADE_AFTER_MS  = 30_000;
const REMOVE_AFTER_MS = 45_000;
const VISIBLE_CARDS = 5;

const KIND_LABELS = {
  rebuttal:      { label: 'Rebuttal',     icon: '🛡️', accent: 'border-rose-500/40 bg-rose-500/10 text-rose-200' },
  carrier_rule:  { label: 'Carrier rule', icon: '📘', accent: 'border-sky-500/40 bg-sky-500/10 text-sky-200' },
  next_question: { label: 'Ask',          icon: '❓', accent: 'border-amber-500/40 bg-amber-500/10 text-amber-200' },
  disclosure:    { label: 'Disclose',     icon: '⚖️', accent: 'border-violet-500/40 bg-violet-500/10 text-violet-200' },
  buying_signal: { label: 'Close',        icon: '🎯', accent: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' },
};

function confidenceDots(c) {
  // 0-1 → 1-5 dots, with a hard floor at 1.
  const n = Math.max(1, Math.min(5, Math.round((c || 0) * 5)));
  return Array.from({ length: 5 }, (_, i) => i < n);
}

export default function CopilotPanel({ activeCallId, active }) {
  const [cards, setCards] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [usedIds, setUsedIds] = useState(() => new Set());
  const timersRef = useRef([]);

  // Subscribe to the suggestion stream. Filter by current callId.
  useEffect(() => {
    const socket = getSocket();
    const onSuggestion = (data) => {
      if (!data || !data.id || !data.text) return;
      if (activeCallId && String(data.callId) !== String(activeCallId)) return;
      setCards((prev) => {
        if (prev.some((c) => c.id === data.id)) return prev; // de-dupe
        return [{ ...data, receivedAt: Date.now() }, ...prev];
      });
    };
    socket.on('copilot:suggestion', onSuggestion);
    return () => socket.off('copilot:suggestion', onSuggestion);
  }, [activeCallId]);

  // Fade + GC.
  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    const now = Date.now();
    for (const c of cards) {
      if (usedIds.has(c.id)) continue;
      const age = now - c.receivedAt;
      timersRef.current.push(setTimeout(() => {
        setCards((prev) => prev.map((x) => (x.id === c.id ? { ...x, _fading: true } : x)));
      }, Math.max(0, FADE_AFTER_MS - age)));
      timersRef.current.push(setTimeout(() => {
        setCards((prev) => prev.filter((x) => x.id !== c.id || usedIds.has(x.id)));
      }, Math.max(0, REMOVE_AFTER_MS - age)));
    }
    return () => timersRef.current.forEach(clearTimeout);
  }, [cards, usedIds]);

  // Clear when call ends and a new one starts.
  useEffect(() => { setCards([]); setUsedIds(new Set()); setExpanded(false); }, [activeCallId]);

  async function markUsed(id) {
    setUsedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try { await apiPost(`/copilot/suggestions/${id}/used`, {}); } catch (_) {
      // Optimistic — if the POST fails we still show "Used" locally. The
      // suggestion's existence is non-critical; reconciliation can happen
      // on the next page load.
    }
  }

  const top = cards.slice(0, VISIBLE_CARDS);
  const overflow = cards.slice(VISIBLE_CARDS);
  const showEmpty = active && cards.length === 0;

  if (!active && cards.length === 0) return null;

  return (
    <div className="rounded-xl border border-civix-border/60 bg-civix-panelLight/60 p-2">
      <div className="flex items-center justify-between px-1 pb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
          Whisper co-pilot <span className="text-civix-purpleSoft normal-case">(beta)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-400 animate-pulse' : 'bg-white/20'}`} />
          <span className="text-[10px] text-white/40">{active ? 'listening' : 'idle'}</span>
        </div>
      </div>

      {showEmpty && (
        <div className="text-[11px] text-white/30 italic px-1 py-2">
          Whisper co-pilot is listening…
        </div>
      )}

      <div className="space-y-1.5">
        {top.map((c) => {
          const meta = KIND_LABELS[c.kind] || { label: c.kind, icon: '💬', accent: 'border-white/20 bg-white/5 text-white/70' };
          const used = usedIds.has(c.id);
          return (
            <div
              key={c.id}
              className={`relative rounded-lg border p-2 transition-all duration-500
                ${used ? 'border-emerald-500/60 bg-emerald-500/10 scale-95 opacity-80' : meta.accent}
                ${c._fading && !used ? 'opacity-40' : ''}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                  <span aria-hidden>{meta.icon}</span>{meta.label}
                </span>
                <div className="flex items-center gap-0.5" aria-label={`confidence ${Math.round((c.confidence || 0) * 100)}%`}>
                  {confidenceDots(c.confidence).map((on, i) => (
                    <span key={i} className={`w-1 h-1 rounded-full ${on ? 'bg-civix-purpleSoft' : 'bg-white/15'}`} />
                  ))}
                </div>
              </div>
              <div className="text-[12px] leading-snug text-white/90 whitespace-pre-wrap">{c.text}</div>
              {!used && (
                <button
                  onClick={() => markUsed(c.id)}
                  className="mt-1.5 text-[10px] font-medium px-2 py-0.5 rounded-md bg-civix-purple/30 hover:bg-civix-purple/50 text-white transition"
                >
                  Used it
                </button>
              )}
              {used && <div className="mt-1 text-[10px] text-emerald-300/80">Marked as used</div>}
            </div>
          );
        })}
      </div>

      {overflow.length > 0 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 text-[10px] text-civix-purpleSoft hover:text-white transition"
        >
          {expanded ? 'Hide older' : `+${overflow.length} more`}
        </button>
      )}

      {expanded && overflow.length > 0 && (
        <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
          {overflow.map((c) => {
            const meta = KIND_LABELS[c.kind] || { label: c.kind, icon: '💬', accent: 'border-white/20 bg-white/5 text-white/70' };
            return (
              <div key={c.id} className="rounded-md border border-white/10 bg-civix-panel/60 p-1.5 opacity-70">
                <div className="text-[9px] uppercase tracking-wider opacity-60 mb-0.5">{meta.icon} {meta.label}</div>
                <div className="text-[11px] text-white/70 leading-snug">{c.text}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
